import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { CA_DATA } from "../../models/data.model";
import { exportCaParPeriodeExcel } from "./ca-par-periode-excel.utils";

/** Palette cyclée sur les lignes du tableau : même code couleur que les
 *  widgets de répartition (doughnut) pour cohérence visuelle. */
const ROW_PALETTE = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
  "#8cd17d", "#a0cbe8",
];

/** Échelle d'agrégation des périodes dans le dialog. Peut différer de celle
 *  utilisée dans le widget (l'utilisateur peut vouloir zoomer sans repasser
 *  au chart). */
export type CaPeriodScale = "j" | "s" | "m" | "a";

export interface CaParPeriodeDialogData {
  /** Données CA brutes (déjà filtrées sur la période par le widget). */
  rawData: CA_DATA["data"];
  /** Scale courante du widget à l'ouverture (utilisée comme valeur initiale). */
  initialPeriod: CaPeriodScale;
  /** Libellé du timeScale courant (ex: "YTD", "6 m") — affiché en sous-titre. */
  timeScaleLabel: string;
}

/** Une facture/entrée CA d'un client dans une période donnée. 1 entrée
 *  correspond à un bucket (date, tiers) dans CA_DATA ; elle peut regrouper
 *  plusieurs factures si le backend agrège, d'où `factureIds: string[]`. */
interface ClientEntry {
  /** Date ISO de l'entrée source. */
  date: string;
  /** Date parsée + formatée pour affichage "15 mars 2026". */
  dateLabel: string;
  montant: number;
  /** Liste des N° / IDs de factures regroupés dans ce bucket (peut être vide
   *  si le backend n'exploite pas `factureIds`). */
  factureIds: string[];
  commandeIds: string[];
}

/** Client d'une période : agrégation (montant, nb factures) + liste des
 *  entrées détaillées pour permettre l'expansion jusqu'à la facture. */
interface ClientRow {
  denomination: string;
  ca: number;
  /** % du client sur le CA TOTAL de la période (pas du dialog). */
  partPeriod: number;
  /** Nombre d'entrées/factures référencées. */
  nbEntries: number;
  entries: ClientEntry[];
}

/** Ligne aggrégée du tableau : 1 ligne = 1 période (jour/semaine/mois/an). */
interface PeriodRow {
  key: string;
  label: string;
  /** Date anchor pour trier : on prend la date min des items de la période. */
  anchor: Date;
  ca: number;
  nbFactures: number;
  nbCommandes: number;
  /** Tous les clients de la période triés par CA décroissant (plus de "top N"
   *  — l'utilisateur veut la vue exhaustive). */
  clients: ClientRow[];
  nbClients: number;
  /** % de cette période sur le CA total dialog. */
  partPct: number;
  /** Couleur de la ligne (palette cyclée par rang de CA). */
  color: string;
}

@Component({
  selector: "vl-ca-par-periode-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    DialogHeaderExportComponent,
    ChipsToggleComponent,
    RadioCardComponent,
    DecimalPipe,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: "./ca-par-periode-dialog.component.html",
  styleUrl: "./ca-par-periode-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaParPeriodeDialogComponent {
  data = inject<CaParPeriodeDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CaParPeriodeDialogComponent>);

  exporting = signal(false);

  // ─── Granularité pilotée depuis le dialog ───────────────────────────────
  scale = signal<CaPeriodScale>(this.data.initialPeriod);
  scaleElements = [
    { name: "Jour", value: "j" as CaPeriodScale },
    { name: "Semaine", value: "s" as CaPeriodScale },
    { name: "Mois", value: "m" as CaPeriodScale },
    { name: "Année", value: "a" as CaPeriodScale },
  ];
  scaleLabel: Record<CaPeriodScale, string> = {
    j: "Jour",
    s: "Semaine",
    m: "Mois",
    a: "Année",
  };

  /** Pluriel pour les libellés ("jours", "semaines", "mois"…). */
  scaleLabelPlural: Record<CaPeriodScale, string> = {
    j: "jours",
    s: "semaines",
    m: "mois",
    a: "années",
  };

  /** Adjectif "meilleur/meilleure" accordé au genre du terme affiché :
   *  jour/mois = masc, semaine/année = fém. Évite les "Meilleure mois". */
  scaleBestLabel: Record<CaPeriodScale, string> = {
    j: "Meilleur jour",
    s: "Meilleure semaine",
    m: "Meilleur mois",
    a: "Meilleure année",
  };

  /** Libellé singulier en minuscule pour intégration dans les phrases
   *  ("moyenne / mois", "1 jour pour 80%"). */
  scaleLabelSingularLower: Record<CaPeriodScale, string> = {
    j: "jour",
    s: "semaine",
    m: "mois",
    a: "année",
  };

  // ─── Tri tableau ────────────────────────────────────────────────────────
  /** Clé de tri courante. */
  sortKey = signal<"date" | "ca" | "nbFactures">("date");
  /** Direction : true = desc, false = asc. */
  sortDesc = signal<boolean>(true);

  /** Clés des périodes actuellement dépliées (pour voir les clients). */
  expandedPeriods = signal<Set<string>>(new Set());
  /** Clés des clients dépliés (format "periodKey::denomination"). */
  expandedClients = signal<Set<string>>(new Set());

  togglePeriod(key: string): void {
    const cur = new Set(this.expandedPeriods());
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    this.expandedPeriods.set(cur);
  }

  isExpanded(key: string): boolean {
    return this.expandedPeriods().has(key);
  }

  clientKey(periodKey: string, denomination: string): string {
    return `${periodKey}::${denomination}`;
  }

  toggleClient(periodKey: string, denomination: string): void {
    const k = this.clientKey(periodKey, denomination);
    const cur = new Set(this.expandedClients());
    if (cur.has(k)) cur.delete(k);
    else cur.add(k);
    this.expandedClients.set(cur);
  }

  isClientExpanded(periodKey: string, denomination: string): boolean {
    return this.expandedClients().has(this.clientKey(periodKey, denomination));
  }

  rows = computed<PeriodRow[]>(() => {
    const period = this.scale();
    const raw = this.data.rawData ?? [];

    const byKey = new Map<
      string,
      {
        anchor: Date;
        ca: number;
        factureIds: Set<string>;
        commandeIds: Set<string>;
        clientEntries: Map<string, ClientEntry[]>;
      }
    >();

    let grandTotal = 0;
    for (const d of raw) {
      const dt = new Date(d.date);
      if (isNaN(dt.getTime())) continue;
      const key = WidgetHelperService.getDateForPeriod(d.date, period);
      if (!key) continue;
      let bucket = byKey.get(key);
      if (!bucket) {
        bucket = {
          anchor: dt,
          ca: 0,
          factureIds: new Set(),
          commandeIds: new Set(),
          clientEntries: new Map(),
        };
        byKey.set(key, bucket);
      }
      if (dt < bucket.anchor) bucket.anchor = dt;
      bucket.ca += d.montant ?? 0;
      for (const fid of d.factureIds ?? []) bucket.factureIds.add(fid);
      for (const cid of d.commandeIds ?? []) bucket.commandeIds.add(cid);
      const client = d.tiersDenomination || "Inconnu";
      const entry: ClientEntry = {
        date: d.date,
        dateLabel: dt.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        montant: d.montant ?? 0,
        factureIds: d.factureIds ?? [],
        commandeIds: d.commandeIds ?? [],
      };
      const arr = bucket.clientEntries.get(client);
      if (arr) arr.push(entry);
      else bucket.clientEntries.set(client, [entry]);
      grandTotal += d.montant ?? 0;
    }

    const out: PeriodRow[] = [];
    for (const [key, b] of byKey) {
      const clients: ClientRow[] = [...b.clientEntries.entries()]
        .map(([denom, entries]) => {
          const ca = entries.reduce((s, e) => s + e.montant, 0);
          return {
            denomination: denom,
            ca,
            partPeriod: b.ca > 0 ? (ca / b.ca) * 100 : 0,
            nbEntries: entries.length,
            entries: entries.sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
            ),
          };
        })
        .sort((a, b) => b.ca - a.ca);
      out.push({
        key,
        label: this.formatPeriodLabel(key, b.anchor, period),
        anchor: b.anchor,
        ca: b.ca,
        nbFactures: b.factureIds.size,
        nbCommandes: b.commandeIds.size,
        clients,
        nbClients: clients.length,
        partPct: grandTotal > 0 ? (b.ca / grandTotal) * 100 : 0,
        color: "",
      });
    }

    const rankedByCA = [...out].sort((a, b) => b.ca - a.ca);
    rankedByCA.forEach((r, i) => {
      r.color = ROW_PALETTE[i % ROW_PALETTE.length];
    });

    return this.sortRows(out);
  });

  /** Vrai si au moins une période a des factureIds renseignés — utilisé pour
   *  afficher/masquer la colonne Factures (backend ne remplit pas toujours). */
  hasFactureData = computed(() => this.rows().some((r) => r.nbFactures > 0));

  /** Stats agrégées : total, moyenne par période, best period, Pareto 80%. */
  stats = computed(() => {
    const rs = this.rows();
    const total = rs.reduce((s, r) => s + r.ca, 0);
    if (!rs.length) {
      return {
        total,
        totalFormatted: "0 €",
        avg: 0,
        avgFormatted: "—",
        bestLabel: "—",
        bestCA: 0,
        bestCAFormatted: "—",
        pareto80: 0,
        count: 0,
      };
    }
    const sortedByCa = [...rs].sort((a, b) => b.ca - a.ca);
    const best = sortedByCa[0];
    let cum = 0;
    let p80 = 0;
    const target = total * 0.8;
    for (const r of sortedByCa) {
      cum += r.ca;
      p80++;
      if (cum >= target) break;
    }
    return {
      total,
      totalFormatted: this.formatEuro(total),
      avg: total / rs.length,
      avgFormatted: this.formatEuro(total / rs.length),
      bestLabel: best.label,
      bestCA: best.ca,
      bestCAFormatted: this.formatEuro(best.ca),
      pareto80: p80,
      count: rs.length,
    };
  });

  private sortRows(rows: PeriodRow[]): PeriodRow[] {
    const key = this.sortKey();
    const desc = this.sortDesc();
    const mult = desc ? -1 : 1;
    return [...rows].sort((a, b) => {
      if (key === "date") {
        return (a.anchor.getTime() - b.anchor.getTime()) * mult;
      }
      if (key === "ca") return (a.ca - b.ca) * mult;
      return (a.nbFactures - b.nbFactures) * mult;
    });
  }

  toggleSort(key: "date" | "ca" | "nbFactures"): void {
    if (this.sortKey() === key) {
      this.sortDesc.set(!this.sortDesc());
    } else {
      this.sortKey.set(key);
      // Valeur par défaut : descendante pour les valeurs numériques, ascendante
      // pour les dates (= ordre chronologique naturel).
      this.sortDesc.set(key !== "date");
    }
  }

  /** Label affichable d'une clé de période (ex. "mars 2026" pour scale='m'). */
  private formatPeriodLabel(
    key: string,
    anchor: Date,
    period: CaPeriodScale,
  ): string {
    switch (period) {
      case "j":
        return anchor.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      case "s":
        // `key` est déjà "Semaine XX - YYYY"
        return key;
      case "m":
        return anchor.toLocaleDateString("fr-FR", {
          month: "long",
          year: "numeric",
        });
      case "a":
        return anchor.toLocaleDateString("fr-FR", { year: "numeric" });
    }
  }

  formatEuro(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return WidgetHelperService.formatNumeric(n) + " €";
  }

  /** Format exact (sans abréviation k/M) pour les cellules CA. */
  formatEuroExact(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  async exportExcel(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportCaParPeriodeExcel({
        rows: this.rows(),
        stats: this.stats(),
        scaleLabel: this.scaleLabel[this.scale()],
        timeScaleLabel: this.data.timeScaleLabel,
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
